import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { recoveryPasswordApi } from '../services';
import type { RootState } from '../store';

/**
 * Solicitudes de recuperación de contraseña hechas desde este navegador
 * (VWA-37). Se persiste a propósito: el bloqueo tiene que sobrevivir a una
 * recarga, o recargar bastaría para saltárselo.
 *
 * Los tiempos son marcas de `Date.now()` que llegan en la acción, para que el
 * reducer sea puro y se pueda probar sin simular el reloj.
 */
export interface RecoveryAttemptState {
  /** Solicitudes desde la última recuperación completa o el último bloqueo. */
  attemptCount: number;
  lastAttemptAt: number | null;
  lockedAt: number | null;
}

const initialState: RecoveryAttemptState = {
  attemptCount: 0,
  lastAttemptAt: null,
  lockedAt: null,
};

export const recoveryAttemptSlice = createSlice({
  name: 'recoveryAttempt',
  initialState,
  reducers: {
    /**
     * Cuenta una solicitud, salga bien o mal, como hace la app: también frena
     * a quien prueba documentos para descubrir a qué usuario corresponden. Al
     * llegar al tope, el contador vuelve a cero y empieza el bloqueo.
     */
    recordRecoveryAttempt: (
      state,
      action: PayloadAction<{ at: number; maxAttempts: number }>
    ) => {
      const { at, maxAttempts } = action.payload;
      const attemptCount = state.attemptCount + 1;

      if (attemptCount >= maxAttempts) {
        state.attemptCount = 0;
        state.lastAttemptAt = null;
        state.lockedAt = at;
        return;
      }

      state.attemptCount = attemptCount;
      state.lastAttemptAt = at;
    },
  },
  extraReducers: builder => {
    /**
     * Una recuperación completa reinicia el conteo. La app no lo hace, y quien
     * recupera su contraseña una vez al mes terminaría bloqueado al tercer mes.
     */
    builder.addMatcher(
      recoveryPasswordApi.endpoints.confirmUsername.matchFulfilled,
      () => initialState
    );
  },
});

export const { recordRecoveryAttempt } = recoveryAttemptSlice.actions;

export const recoveryAttemptReducer = recoveryAttemptSlice.reducer;

export const selectRecoveryAttempt = (state: RootState) =>
  state.recoveryAttempt;
